import { getAuthHeaders } from "./authHeaders";

export interface BulkTransferResultItem {
  id: number;
  status: "completed" | "failed";
  targetPaybackId?: number;
  error?: string;
}

export interface BulkTransferProgressState {
  open: boolean;
  total: number;
  processed: number;
  completed: number;
  failed: number;
  percent: number;
  status: "starting" | "running" | "completed" | "failed";
  message: string;
  error?: string;
  results?: BulkTransferResultItem[];
}

export async function executeBulkTransfer(
  ids: number[],
  apiUrl: string,
  onProgress: (state: BulkTransferProgressState) => void,
  onComplete?: () => void,
): Promise<void> {
  const state: BulkTransferProgressState = {
    open: true,
    total: ids.length,
    processed: 0,
    completed: 0,
    failed: 0,
    percent: 0,
    status: "starting",
    message: `Initiating transfer for ${ids.length} process(es)...`,
    results: [],
  };
  onProgress({ ...state });

  try {
    const res = await fetch(`${apiUrl}/purchasepaybacks/bulk-transfer`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.message || err?.error?.message || `Server returned ${res.status}`);
    }

    const { batchId } = await res.json();
    state.status = "running";
    state.message = `Transferring ${ids.length} process(es) in batches of 50...`;
    onProgress({ ...state });

    const pollInterval = 1500;
    const poll = async (): Promise<void> => {
      try {
        const statusRes = await fetch(`${apiUrl}/purchasepaybacks/bulk-transfer/${batchId}/status`, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (statusRes.ok) {
          const data = await statusRes.json();
          state.completed = data.completed ?? 0;
          state.failed = data.failed ?? 0;
          state.processed = data.processed ?? (state.completed + state.failed);
          state.total = data.total ?? ids.length;
          state.percent = Math.min(100, Math.floor((state.processed / state.total) * 100));
          state.message = `Processed ${state.processed} of ${state.total} (${state.percent}%)`;
          if (Array.isArray(data.results)) {
            state.results = data.results;
          }
          onProgress({ ...state });

          if (data.status === "completed" || data.status === "failed" || state.processed >= state.total) {
            state.status = data.status === "failed" && state.completed === 0 ? "failed" : "completed";
            state.percent = 100;
            state.message = `Transfer completed (${state.completed} transferred, ${state.failed} failed)`;
            onProgress({ ...state });
            if (onComplete) onComplete();
            return;
          }
        }
      } catch (err: any) {
        console.warn("Error polling bulk transfer status:", err?.message);
      }
      setTimeout(poll, pollInterval);
    };

    setTimeout(poll, 1000);
  } catch (e: any) {
    const errorMsg: string = (e && typeof e.message === "string" && e.message) ? e.message : "Failed to start bulk transfer";
    state.status = "failed";
    state.error = errorMsg;
    state.message = errorMsg;
    onProgress({ ...state });
  }
}
